import { memo, type MutableRefObject, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { ArrowUpIcon } from '@m/alchemy-ui/icons/ArrowUpIcon';
import { ArrowDownIcon } from '@m/alchemy-ui/icons/ArrowDownIcon';
import { type PanelConfigTypename, type PanelTypeOption, panelTypeOptions } from '../common/panel-types';
import {
  AddElementHint,
  AddElementItem,
  AddElementItemIcon,
  AddElementMenu as AddElementMenuList,
  AddElementTriggerContent,
  SecondaryButton
} from '../CustomPage.styled';

const messages = defineMessages({
  addElement: {
    id: 'editCustomPageAddElement',
    defaultMessage: 'Add element'
  },
  addElementHint: {
    id: 'editCustomPageAddElementHint',
    defaultMessage: 'Drag the element onto the page, or select it to choose a position.'
  }
});

interface AddElementOptionProps {
  readonly option: PanelTypeOption;
  readonly closeMenuRef: MutableRefObject<(() => void) | null>;
  readonly onPlace?: (typename: PanelConfigTypename) => void;
}

const AddElementOption = ({ option, closeMenuRef, onPlace }: AddElementOptionProps) => {
  const intl = useIntl();
  const itemRef = useRef<HTMLButtonElement>(null);
  const { typename } = option;

  useEffect(() => {
    const element = itemRef.current;

    if (!element) {
      return undefined;
    }

    return draggable({
      element,
      getInitialData: () => ({ type: 'new-panel', typename }),
      // onDragStart fires after the native dragstart completes, so unmounting this source by closing the menu is safe.
      onDragStart: () => closeMenuRef.current?.()
    });
  }, [typename, closeMenuRef]);

  return (
    <AddElementItem
      ref={itemRef}
      type="button"
      aria-describedby="page-cms-add-element-hint"
      data-testid={`add-element-option-${option.id}`}
      onClick={onPlace && (() => onPlace(typename))}
    >
      <AddElementItemIcon>{option.icon}</AddElementItemIcon>
      <span>{intl.formatMessage(option.message)}</span>
    </AddElementItem>
  );
};

interface AddElementMenuProps {
  readonly onPlace?: (typename: PanelConfigTypename, trigger: HTMLElement) => void;
}

const AddElementMenuComponent = ({ onPlace }: AddElementMenuProps) => {
  const intl = useIntl();
  const closeMenuRef = useRef<(() => void) | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <Popper
      placement="bottom-start"
      trigger={({ isOpen }) => (
        <SecondaryButton ref={triggerRef} type="button" priority="secondary" data-testid="add-element-button">
          <AddElementTriggerContent>
            {intl.formatMessage(messages.addElement)}
            {isOpen ? <ArrowUpIcon aria-hidden="true" /> : <ArrowDownIcon aria-hidden="true" />}
          </AddElementTriggerContent>
        </SecondaryButton>
      )}
      overlay={({ close, closeAndReturnFocus }) => {
        closeMenuRef.current = close;

        const handlePlace =
          onPlace &&
          ((typename: PanelConfigTypename) => {
            closeAndReturnFocus();

            if (triggerRef.current) {
              onPlace(typename, triggerRef.current);
            }
          });

        return (
          <PopperBox hasArrow maxWidth={210}>
            <AddElementMenuList data-testid="add-element-menu">
              <AddElementHint id="page-cms-add-element-hint">
                {intl.formatMessage(messages.addElementHint)}
              </AddElementHint>
              {panelTypeOptions.map((option) => (
                <AddElementOption
                  key={option.typename}
                  option={option}
                  closeMenuRef={closeMenuRef}
                  onPlace={handlePlace}
                />
              ))}
            </AddElementMenuList>
          </PopperBox>
        );
      }}
    />
  );
};

export const AddElementMenu = memo(AddElementMenuComponent);
